import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const DesktopAgentSetup = () => {
  const [agentStatus, setAgentStatus] = useState('offline'); // 'online' or 'offline'
  const [lastSync, setLastSync] = useState(null);

  // Optional: Poll the backend to check if the agent is actively running
  useEffect(() => {
    const checkStatus = async () => {
      try {
        // You would create a simple endpoint like GET /api/agent/status
        // that checks the last time the HR's script pinged the server.
        // For now, we simulate the check:
        const response = await api.get('/api/agent/status').catch(() => ({ data: { status: 'offline' }}));
        setAgentStatus(response.data.status);
        setLastSync(response.data.lastSync);
      } catch (err) {
        setAgentStatus('offline');
      }
    };
    
    checkStatus();
    const interval = setInterval(checkStatus, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const handleDownload = (os) => {
    // In production, these would link to your AWS S3 bucket where the .exe and .dmg are stored
    const fileUrl = os === 'windows' 
      ? 'https://your-s3-bucket.com/downloads/SmartInterview_Sync.exe' 
      : 'https://your-s3-bucket.com/downloads/SmartInterview_Sync_Mac.dmg';
      
    window.open(fileUrl, '_blank');
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h2 style={styles.title}>⚙️ Screening Automation</h2>
        <p style={styles.subtitle}>
          Never upload a resume manually again. Install the SmartInterview Desktop Agent to automatically sync and AI-score resumes the moment you save them to your computer.
        </p>
      </header>

      {/* STATUS INDICATOR */}
      <div style={{...styles.statusCard, borderColor: agentStatus === 'online' ? '#10b981' : '#ef4444'}}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ 
            width: '12px', height: '12px', borderRadius: '50%', 
            background: agentStatus === 'online' ? '#10b981' : '#ef4444',
            animation: agentStatus === 'online' ? 'pulse 2s infinite' : 'none'
          }} />
          <div>
            <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>
              Desktop Agent Status: {agentStatus === 'online' ? 'Online & Connected' : 'Not Connected'}
            </h4>
            {lastSync && <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>Last sync: {lastSync}</p>}
          </div>
        </div>
      </div>

      {/* DOWNLOAD SECTION */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Step 1: Download the Desktop Agent</h3>
        <p style={styles.cardText}>Choose your operating system to download the lightweight background application.</p>
        
        <div style={styles.buttonGroup}>
          <button style={styles.primaryButton} onClick={() => handleDownload('windows')}>
            <span style={{ fontSize: '20px' }}>🪟</span> Download for Windows (.exe)
          </button>
          
          <button style={styles.secondaryButton} onClick={() => handleDownload('mac')}>
            <span style={{ fontSize: '20px' }}>🍎</span> Download for Mac (.dmg)
          </button>
        </div>
      </div>

      {/* INSTRUCTIONS SECTION */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Step 2: How it works (3-Step Setup)</h3>
        
        <div style={styles.stepsGrid}>
          <div style={styles.stepBox}>
            <div style={styles.stepNumber}>1</div>
            <h4>Install & Run</h4>
            <p style={styles.stepText}>Double-click the downloaded file. It will install and run silently in the background of your computer.</p>
          </div>
          
          <div style={styles.stepBox}>
            <div style={styles.stepNumber}>2</div>
            <h4>Locate your new folder</h4>
            <p style={styles.stepText}>The app automatically creates a folder on your computer's Desktop called <code style={styles.code}>SmartInterview_Resumes</code>.</p>
          </div>
          
          <div style={styles.stepBox}>
            <div style={styles.stepNumber}>3</div>
            <h4>Just Save Files!</h4>
            <p style={styles.stepText}>Whenever you receive a PDF resume via email or LinkedIn, just save it into that folder. We handle the rest!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '24px', maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' },
  header: { marginBottom: '8px' },
  title: { fontSize: '28px', fontWeight: '800', margin: '0 0 8px 0' },
  subtitle: { fontSize: '15px', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.5' },
  statusCard: {
    padding: '16px 24px', borderRadius: '12px', border: '2px solid',
    background: 'var(--bg-card-glass)', boxShadow: 'var(--shadow-sm)'
  },
  card: {
    padding: '24px', borderRadius: '16px', border: '1px solid var(--border-light)',
    background: 'var(--bg-card-glass)', boxShadow: 'var(--shadow-sm)'
  },
  cardTitle: { margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700' },
  cardText: { margin: '0 0 20px 0', color: 'var(--text-secondary)', fontSize: '14px' },
  buttonGroup: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  primaryButton: {
    display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 24px',
    background: 'var(--primary-color, #3b82f6)', color: 'white', border: 'none',
    borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: '0.2s'
  },
  secondaryButton: {
    display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 24px',
    background: 'var(--bg-input, #f3f4f6)', color: 'var(--text-primary, #111827)', border: '1px solid var(--border-light, #e5e7eb)',
    borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: '0.2s'
  },
  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginTop: '16px' },
  stepBox: { padding: '20px', background: 'rgba(0,0,0,0.02)', borderRadius: '12px', border: '1px solid var(--border-light)' },
  stepNumber: { 
    width: '32px', height: '32px', borderRadius: '50%', background: 'var(--primary-color, #3b82f6)', 
    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', 
    fontWeight: 'bold', fontSize: '16px', marginBottom: '12px'
  },
  stepText: { fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', marginTop: '8px' },
  code: { background: 'rgba(0,0,0,0.05)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace' }
};

export default DesktopAgentSetup;
